import { Pill, Package, Tablets, Syringe, Droplets, Pipette, FlaskConical, Beaker } from 'lucide-react';

// What each dosage form looks like, in one place. It lived inside MedicinesManagement, so every
// other screen showing a medicine fell back to a single generic pill: an invoice printed the same
// icon against a syrup and a capsule, which is what prompted moving it here.
//
// Object key order is preserved by modern JS engines, so iteration order matches declaration
// order. Keys here are sorted alphabetically by label; OTHER stays at the end.
// Each type carries a lucide icon (rendered white inside the gradient avatar tile) — same
// pattern the rest of the app uses for category configs (see VENDOR_TYPE_CONFIG). No emoji.
export const MEDICINE_TYPE_CONFIG = {
  CAPSULE: { label: 'Capsule', icon: Pill, color: 'from-purple-500 to-violet-600' },
  CREAM: { label: 'Cream', icon: Droplets, color: 'from-pink-500 to-rose-600' },
  DROPS: { label: 'Drops', icon: Pipette, color: 'from-sky-500 to-blue-600' },
  GEL: { label: 'Gel', icon: Droplets, color: 'from-emerald-500 to-green-600' },
  INHALER: { label: 'Inhaler', icon: Beaker, color: 'from-indigo-500 to-blue-600' },
  INJECTION: { label: 'Injection', icon: Syringe, color: 'from-red-500 to-rose-600' },
  LOTION: { label: 'Lotion', icon: Droplets, color: 'from-rose-500 to-pink-600' },
  OINTMENT: { label: 'Ointment', icon: Droplets, color: 'from-teal-500 to-cyan-600' },
  PATCH: { label: 'Patch', icon: Package, color: 'from-amber-500 to-yellow-600' },
  POWDER: { label: 'Powder', icon: Beaker, color: 'from-yellow-500 to-amber-600' },
  SACHET: { label: 'Sachet', icon: Package, color: 'from-orange-500 to-red-600' },
  SOLUTION: { label: 'Solution', icon: FlaskConical, color: 'from-cyan-500 to-teal-600' },
  SPRAY: { label: 'Spray', icon: Droplets, color: 'from-sky-500 to-cyan-600' },
  SUPPOSITORY: { label: 'Suppository', icon: Pill, color: 'from-violet-500 to-purple-600' },
  SUSPENSION: { label: 'Suspension', icon: FlaskConical, color: 'from-lime-500 to-emerald-600' },
  SYRUP: { label: 'Syrup', icon: FlaskConical, color: 'from-amber-500 to-orange-600' },
  TABLET: { label: 'Tablet', icon: Tablets, color: 'from-blue-500 to-indigo-600' },
  // Consumable supplies. Same map so a supply row renders a badge and icon like anything else;
  // which of these are OFFERED is decided per catalogue (see CATALOGUE in MedicinesManagement).
  SYRINGE: { label: 'Syringe', icon: Syringe, color: 'from-red-500 to-rose-600' },
  GLOVES: { label: 'Gloves', icon: Package, color: 'from-lime-500 to-green-600' },
  TEST_STRIP: { label: 'Test Strip', icon: Pipette, color: 'from-fuchsia-500 to-pink-600' },
  DRESSING: { label: 'Dressing', icon: Package, color: 'from-orange-500 to-amber-600' },
  MASK: { label: 'Mask', icon: Package, color: 'from-cyan-500 to-sky-600' },
  TUBING: { label: 'Tubing / Catheter', icon: Package, color: 'from-slate-500 to-gray-600' },
  SUTURE: { label: 'Suture', icon: Package, color: 'from-purple-500 to-fuchsia-600' },
  CONSUMABLE: { label: 'Consumable', icon: Package, color: 'from-stone-500 to-neutral-600' },
  OTHER: { label: 'Other', icon: Package, color: 'from-gray-500 to-slate-600' }
};

/**
 * The icon for a dosage form, for screens that hold the form as a loose string rather than a
 * catalogue row (an invoice line carries "SYRUP" copied from the prescription). Unknown or missing
 * forms fall back rather than render nothing, because a row still needs its icon.
 */
export const medicineTypeIcon = (type, fallback = Pill) =>
  MEDICINE_TYPE_CONFIG[String(type || '').trim().toUpperCase()]?.icon || fallback;
